import z from "zod";
import type { ColorSelectionResetIneligibleReason } from "@remi/enums/color-selection-reset-ineligible-reason";
import type { HistoryOrigin } from "@remi/enums/history-origin";
import type { TaskDisposition } from "@remi/enums/task/disposition";
import type { TaskStatus } from "@remi/enums/task-status";
import type { TaskType } from "@remi/enums/task-type";

import { ErrorClassification } from "../../../sentry/classification";
import type { ErrorDetails } from "../domain";

const taskErrorTypeValues = [
  "TASK_NOT_FOUND",
  "TASK_OF_TYPE_NOT_FOUND",
  "TASK_ALREADY_COMPLETED",
  "TASK_ALREADY_IN_PROGRESS",
  "TASK_INVALID_STATUS_TRANSITION",
  "TASK_COMPLETION_FAILED",
  "TASK_COMPLETION_DATE_MISSING",
  "TASK_RESET_FAILED",
  "TASK_ATTEMPT_FAILED",
  "RELEASE_TASK_DELETED",
  "RECORD_ATTEMPT_DEDUPE_KEY_CONFLICT",
  "RECORD_ATTEMPT_UNKNOWN_REASON",
  "RECORD_ATTEMPT_TASK_EVENT_NOT_FOUND",
  "RECORD_ATTEMPT_REASON_UNREADABLE",
  "RETIRED_REASON_BACKFILL_SLA_MATCHERS",
  "INSTALL_RESCHEDULE_IN_TRANSACTION",
  "STANDING_HOLDUP_RESOLVE_UNSUPPORTED",
  "RECORD_ATTEMPT_NOTE_CONTRACTOR_ORG_UNRESOLVED",
  "TASK_ATTEMPT_NOT_FOUND",
  "LADDER_NOT_DECLARED_FOR_CANDIDATE",
  "PAYER_STAGE_SIGNAL_TASK_TYPE_UNSUPPORTED",
  "ATTEMPT_CONTEXT_NOT_ELIGIBLE",
  "TASK_EVALUATION_FAILED",
  "TASK_AUTO_COMPLETE_FAILED",
  "WORKFLOW_VERSION_NOT_FOUND",
  "WORKFLOW_EVALUATION_FAILED",
  "WORKFLOW_INVALID_SEQUENCE",
  "INVALID_TASK_ATTEMPT_DATA",
  "WORKFLOW_DEPENDENCY_NOT_MET",
  "WORKFLOW_PHASE_TRANSITION_FAILED",
  "NO_ACTIVE_WORKFLOW",
  "PROJECT_NOT_FOUND",
  "PROJECT_EVALUATION_FAILED",
  "WORK_ORDER_OR_FSD_NOT_FOUND",
  "PROJECT_MISSING_BID_REQUEST_ID",
  "PROJECT_TO_BID_REQUEST_NOT_FOUND",
  "BID_NOT_FOUND",
  "BID_RFQ_NOT_FOUND",
  "QUOTE_VERSION_NOT_FOUND",
  "REQUEST_FOR_QUOTE_NOT_FOUND",
  "PROJECT_MISSING_PARTNER_FLOW",
  "SITE_VISIT_PROJECT_MISSING_BID_PARTNER_FLOW",
  "TASK_MISSING_REQUIRED_DATA",
  "TASK_ORPHANED_RECORD",
  "TASK_DATA_CORRUPTION",
  "TASK_CONCURRENT_MODIFICATION",
  "TASK_COMPLETION_RACE_LOST",
  "TERMINAL_ATTEMPT_DISPOSITION_OVERTAKEN",
  "TASK_COMPLETION_PRECONDITION_FAILED",
  "UNKNOWN_COMPLETION_STEP",
  "COMPLETION_EFFECT_KEYS_MISMATCH",
  "TASK_DISPOSITION_NOT_ALLOWED_FOR_TYPE",
  "SOW_CHANGE_ABANDONED_WITHOUT_DISPOSITION",
  "TASK_FILE_UPLOAD_FAILED",
  "TASK_FILE_PROCESSING_FAILED",
  "TASK_FILE_NOT_FOUND",
  "INVALID_FILE_DOWNLOAD_URL",
  "TASK_S3_OPERATION_FAILED",
  "SUNRUN_API_ERROR",
  "SUNRUN_AUTOMATION_FAILED",
  "HUBSPOT_SYNC_FAILED",
  "SLACK_NOTIFICATION_FAILED",
  "SMS_DELIVERY_FAILED",
  "EMAIL_DELIVERY_FAILED",
  "QUICKBOOKS_SYNC_FAILED",
  "ARCASA_WORKFLOW_FAILED",
  "TASK_PERMISSION_DENIED",
  "TASK_VALIDATION_FAILED",
  "TASK_BUSINESS_RULE_VIOLATION",
  "TASK_DEADLINE_CALCULATION_FAILED",
  "TASK_SCHEDULE_CONFLICT",
  "TASK_SLA_VIOLATION",
  "TASK_CREATE_FAILED",
  "TASK_UPDATE_FAILED",
  "TASK_DELETE_FAILED",
  "TASK_BULK_OPERATION_FAILED",
  "TASK_TRANSACTION_FAILED",
  "TASK_COMPLETION_ADMISSION_READ_FAILED",
  "TASK_NOT_A_BLOCKING_TASK",
  "TASK_NOT_ON_BID_PROJECT",
  "PROPERTY_MODEL_REQUIRED",
  "SITE_VISIT_NOT_ON_TASK_WORKSITE",
  "BID_PROJECT_NOT_FOUND",
  "NO_PROJECT_FLOW_FOUND_FOR_SOW_TYPE",
  "TARGET_VERSION_NOT_FOUND",
  "MIGRATION_SCRIPT_NOT_FOUND",
  "WORKFLOW_DEFINITION_MISSING_TASK",
  "INVOICE_AUTOMATION_CREATION_FAILED",
  "AUTOMATION_ID_NOT_FOUND",
  "PARTNER_FLOW_BUYER_MISMATCH",
  "INVOICE_BUCKET_NOT_FOUND",
  "INSTALL_COMPLETED_DATE_BEFORE_STARTED_DATE",
  "INSTALL_COMPLETED_DATE_IN_FUTURE",
  "REVIEW_REVENUE_ESCROW_NOT_FUNDED",
  "REVIEW_REVENUE_HELD",
  "FINAL_INSPECTION_SCHEDULED_HELD",
  "REVIEW_REVENUE_AUTO_COMPLETE_UNAUTHORIZED",
  "PARTNER_FLOW_NOT_FOUND",
  "SUNRUN_REDESIGN_WORK_ORDER_NOT_FOUND",
  "SUNRUN_REDESIGN_PROJECT_RECORD_NOT_FOUND",
  "INSURANCE_ESTIMATE_CREATED_SOFT_DELETED",
  "UNEXPECTED_ESTIMATE_TASK_TYPE",
  "TASK_TYPE_MISMATCH",
  "RETIRED_TASK_TYPE_REJECTED",
  "RETIRED_BLOCKER_TASK_TYPE_REJECTED",
  "UNAUTHORIZED_GROUP_COLUMN",
  "VERIFY_COST_BILLS_PENDING_REVIEW",
  "PANEL_MARKUP_REQUIRED",
  "TASK_SUMMARY_NOT_FOUND",
  "TASK_SUMMARY_UNAVAILABLE",
  "TASK_UNBLOCK_REFUSED",
  "TASK_UNBLOCK_REFUSED_SOW_CHANGE_HOLD",
  "TASK_HELD_UP",
  "SITE_VISIT_PROJECT_NOT_RELATED_TO_TASK",
  "COLOR_SELECTION_RESET_INELIGIBLE",
  "INSTALL_SCHEDULED_DATE_REQUIRED",
  "NTP_FORCE_COMPLETE_ALL_RFQS_REJECTED",
  "UNKNOWN",
  "TASK_LIST_CURSOR_INVALID",
] as const;

const taskErrorTypeSchema = z.enum(taskErrorTypeValues);
const TaskErrorType = taskErrorTypeSchema.enum;
type TaskErrorType = z.infer<typeof taskErrorTypeSchema>;

export type TaskErrors =
  | {
      errorType: typeof TaskErrorType.TASK_NOT_FOUND;
      data: { taskId: string } | { projectId: number; taskType: TaskType };
    }
  | {
      errorType: typeof TaskErrorType.TASK_OF_TYPE_NOT_FOUND;
      data: { taskType: string; projectId: number };
    }
  | {
      errorType: typeof TaskErrorType.TASK_HELD_UP;
      data: {
        taskId: string;
        projectId: number;
        openHoldupCount: number;
        openConcessionId: string | null;
        openReviewTaskId: string | null;
      };
    }
  | {
      errorType: typeof TaskErrorType.RELEASE_TASK_DELETED;
      data: { taskId: string };
    }
  | {
      errorType: typeof TaskErrorType.TASK_ALREADY_COMPLETED;
      data: { taskId: string; completedAt: Date; attemptedBy: string };
    }
  | {
      errorType: typeof TaskErrorType.TASK_ALREADY_IN_PROGRESS;
      data: { taskId: string; currentAssignee: string; startedAt: Date };
    }
  | {
      errorType: typeof TaskErrorType.TASK_INVALID_STATUS_TRANSITION;
      data: {
        taskId: string;
        currentStatus: string;
        attemptedStatus: string;
        allowedTransitions: string[];
      };
    }
  | {
      errorType: typeof TaskErrorType.TASK_COMPLETION_FAILED;
      data: {
        taskId: string;
        taskType: string;
        reason: string;
        projectId: number;
      };
    }
  | {
      errorType: typeof TaskErrorType.TASK_COMPLETION_DATE_MISSING;
      data: {
        taskId: string;
      };
    }
  | {
      errorType: typeof TaskErrorType.TASK_RESET_FAILED;
      data: {
        taskId: string;
        taskType: string;
        reason: string;
      };
    }
  | {
      errorType: typeof TaskErrorType.TASK_ATTEMPT_FAILED;
      data: {
        taskId: string;
        taskType: string;
        attemptNumber: number;
        reason: string;
      };
    }
  | {
      errorType: typeof TaskErrorType.RECORD_ATTEMPT_DEDUPE_KEY_CONFLICT;
      data: {
        taskId: string;
        taskEventId: string;
        dedupeKey: string;
        conflictingFields: string;
      };
    }
  | {
      errorType: typeof TaskErrorType.RECORD_ATTEMPT_UNKNOWN_REASON;
      data: {
        taskId: string;
        dedupeKey: string;
        reason: string;
      };
    }
  | {
      errorType: typeof TaskErrorType.RECORD_ATTEMPT_TASK_EVENT_NOT_FOUND;
      data: {
        taskEventId: string;
      };
    }
  | {
      errorType: typeof TaskErrorType.RECORD_ATTEMPT_REASON_UNREADABLE;
      data: {
        taskEventId: string;
        taskId: string;
      };
    }
  | {
      errorType: typeof TaskErrorType.RETIRED_REASON_BACKFILL_SLA_MATCHERS;
      data: {
        slaMatcherIds: string[];
      };
    }
  | {
      errorType: typeof TaskErrorType.INSTALL_RESCHEDULE_IN_TRANSACTION;
      data: {
        taskId: string;
      };
    }
  | {
      errorType: typeof TaskErrorType.STANDING_HOLDUP_RESOLVE_UNSUPPORTED;
      data: {
        taskEventId: string;
        taskId: string;
        taskType: string;
        reason: string;
      };
    }
  | {
      errorType: typeof TaskErrorType.ATTEMPT_CONTEXT_NOT_ELIGIBLE;
      data: {
        taskId: string;
        taskType: TaskType;
        taskStatus: TaskStatus;
      };
    }
  | {
      errorType: typeof TaskErrorType.RECORD_ATTEMPT_NOTE_CONTRACTOR_ORG_UNRESOLVED;
      data: {
        taskEventId: string;
        taskId: string;
        projectId: number;
      };
    }
  | {
      errorType: typeof TaskErrorType.TASK_ATTEMPT_NOT_FOUND;
      data: {
        taskId?: string;
        taskAttemptId?: string;
      };
    }
  | {
      errorType: typeof TaskErrorType.LADDER_NOT_DECLARED_FOR_CANDIDATE;
      data: {
        taskId: string;
        taskType: string;
      };
    }
  | {
      errorType: typeof TaskErrorType.PAYER_STAGE_SIGNAL_TASK_TYPE_UNSUPPORTED;
      data: { taskId: string; taskType: string | null };
    }
  | {
      errorType: typeof TaskErrorType.TASK_EVALUATION_FAILED;
      data: {
        taskId: string;
        evaluationType: string;
        reason: string;
      };
    }
  | {
      errorType: typeof TaskErrorType.TASK_AUTO_COMPLETE_FAILED;
      data: {
        taskId: string;
        taskType: string;
        handlerName: string;
        reason: string;
      };
    }
  | {
      errorType: typeof TaskErrorType.WORKFLOW_VERSION_NOT_FOUND;
      data: { projectId?: number; workflowId?: string; workflowVersion?: number };
    }
  | {
      errorType: typeof TaskErrorType.WORKFLOW_EVALUATION_FAILED;
      data: {
        projectId: number;
        workflowVersionId: string;
        evaluationStep: string;
        reason: string;
      };
    }
  | {
      errorType: typeof TaskErrorType.WORKFLOW_INVALID_SEQUENCE;
      data: {
        projectId: number;
        currentTask: string;
        attemptedTask: string;
        requiredTasks: string[];
      };
    }
  | {
      errorType: typeof TaskErrorType.WORKFLOW_DEPENDENCY_NOT_MET;
      data: {
        taskId: string;
        dependencyTaskIds: number[];
        unmetDependencies: number[];
      };
    }
  | {
      errorType: typeof TaskErrorType.WORKFLOW_PHASE_TRANSITION_FAILED;
      data: {
        projectId: number;
        currentPhase: string;
        targetPhase: string;
        reason: string;
      };
    }
  | {
      errorType: typeof TaskErrorType.NO_ACTIVE_WORKFLOW;
      data: undefined;
    }
  | {
      errorType: typeof TaskErrorType.PROJECT_NOT_FOUND;
      data: { projectId: number; taskId?: string };
    }
  | {
      errorType: typeof TaskErrorType.PROJECT_EVALUATION_FAILED;
      data: {
        projectId: number;
        evaluationType: string;
        reason: string;
      };
    }
  | {
      errorType: typeof TaskErrorType.PROJECT_MISSING_BID_REQUEST_ID;
      data: {
        projectId: number;
      };
    }
  | {
      errorType: typeof TaskErrorType.PARTNER_FLOW_NOT_FOUND;
      data: {
        taskId: string;
      };
    }
  | {
      errorType: typeof TaskErrorType.WORK_ORDER_OR_FSD_NOT_FOUND;
      data: {
        projectId: number;
        bidRequestId: string;
      };
    }
  | {
      errorType: typeof TaskErrorType.PROJECT_TO_BID_REQUEST_NOT_FOUND;
      data: {
        projectId: number;
      };
    }
  | {
      errorType: typeof TaskErrorType.BID_NOT_FOUND;
      data: {
        bidId?: number;
        projectId?: number;
        taskId?: string;
      };
    }
  | {
      errorType: typeof TaskErrorType.BID_RFQ_NOT_FOUND;
      data: {
        bidId: number;
        projectId: number;
      };
    }
  | {
      errorType: typeof TaskErrorType.QUOTE_VERSION_NOT_FOUND;
      data: { quoteVersionId: string };
    }
  | {
      errorType: typeof TaskErrorType.REQUEST_FOR_QUOTE_NOT_FOUND;
      data: {
        requestForQuoteId?: number;
        projectId?: number;
        taskId?: string;
      };
    }
  | {
      errorType: typeof TaskErrorType.PROJECT_MISSING_PARTNER_FLOW;
      data: {
        projectId: number;
      };
    }
  | {
      errorType: typeof TaskErrorType.SITE_VISIT_PROJECT_MISSING_BID_PARTNER_FLOW;
      data: {
        projectId: number;
      };
    }
  | {
      errorType: typeof TaskErrorType.SITE_VISIT_PROJECT_NOT_RELATED_TO_TASK;
      data: { confirmSowTaskId: string; siteVisitProjectId: number };
    }
  | {
      errorType: typeof TaskErrorType.TASK_MISSING_REQUIRED_DATA;
      data: {
        taskId: string;
        missingFields: string[];
        taskType: string;
      };
    }
  | {
      errorType: typeof TaskErrorType.TASK_ORPHANED_RECORD;
      data: {
        taskId: string;
        missingRelation: string;
        expectedId: number;
      };
    }
  | {
      errorType: typeof TaskErrorType.TASK_DATA_CORRUPTION;
      data: {
        taskId: string;
        corruptedField: string;
        expectedType: string;
        actualValue: unknown;
      };
    }
  | {
      errorType: typeof TaskErrorType.TASK_CONCURRENT_MODIFICATION;
      data: {
        taskId: string;
        expectedVersion: number;
        actualVersion: number;
      };
    }
  | {
      errorType: typeof TaskErrorType.TASK_COMPLETION_RACE_LOST;
      data: {
        taskId: string;
        projectId: number;
      };
    }
  | {
      errorType: typeof TaskErrorType.TERMINAL_ATTEMPT_DISPOSITION_OVERTAKEN;
      data: {
        taskId: string;
        taskEventId: string;
        disposition: TaskDisposition;
        winningDisposition: TaskDisposition;
      };
    }
  | {
      errorType: typeof TaskErrorType.TASK_COMPLETION_PRECONDITION_FAILED;
      data: {
        taskId: string;
        projectId: number;
      };
    }
  | {
      errorType: typeof TaskErrorType.UNKNOWN_COMPLETION_STEP;
      data: {
        stepName: string;
        knownSteps: string[];
      };
    }
  | {
      errorType: typeof TaskErrorType.COMPLETION_EFFECT_KEYS_MISMATCH;
      data: {
        taskId: string;
        taskType: string;
        snapshotKeys: string[];
        currentKeys: string[];
      };
    }
  | {
      errorType: typeof TaskErrorType.TASK_DISPOSITION_NOT_ALLOWED_FOR_TYPE;
      data: {
        taskId: string;
        taskType: TaskType;
        disposition: TaskDisposition;
      };
    }
  | {
      errorType: typeof TaskErrorType.SOW_CHANGE_ABANDONED_WITHOUT_DISPOSITION;
      data: {
        taskId: string;
        projectId: number;
        origin: HistoryOrigin;
      };
    }
  | {
      errorType: typeof TaskErrorType.TASK_FILE_UPLOAD_FAILED;
      data: {
        taskId: string;
        fileName: string;
        fileSize: number;
        reason: string;
      };
    }
  | {
      errorType: typeof TaskErrorType.TASK_FILE_PROCESSING_FAILED;
      data: {
        taskId: string;
        fileId: string;
        processingStep: string;
        reason: string;
      };
    }
  | {
      errorType: typeof TaskErrorType.TASK_FILE_NOT_FOUND;
      data: {
        taskId: string;
        fileId: string;
        fileName?: string;
      };
    }
  | {
      errorType: typeof TaskErrorType.INVALID_FILE_DOWNLOAD_URL;
      data: { refId: string; url: string };
    }
  | {
      errorType: typeof TaskErrorType.TASK_S3_OPERATION_FAILED;
      data: {
        operation: "upload" | "download" | "delete" | "getSignedUrl";
        bucket: string;
        key: string;
        reason: string;
      };
    }
  | {
      errorType: typeof TaskErrorType.SUNRUN_API_ERROR;
      data: {
        operation: string;
        sunrunProjectId?: string;
        httpStatus?: number;
        errorMessage: string;
      };
    }
  | {
      errorType: typeof TaskErrorType.SUNRUN_AUTOMATION_FAILED;
      data: {
        automationType: string;
        projectId: number;
        sunrunProjectId?: string;
        reason: string;
      };
    }
  | {
      errorType: typeof TaskErrorType.HUBSPOT_SYNC_FAILED;
      data: {
        operation: string;
        hubspotId?: string;
        entityType: string;
        reason: string;
      };
    }
  | {
      errorType: typeof TaskErrorType.SLACK_NOTIFICATION_FAILED;
      data: {
        channel: string;
        messageType: string;
        reason: string;
      };
    }
  | {
      errorType: typeof TaskErrorType.SMS_DELIVERY_FAILED;
      data: {
        phoneNumber: string;
        messageType: string;
        twilioErrorCode?: string;
        reason: string;
      };
    }
  | {
      errorType: typeof TaskErrorType.EMAIL_DELIVERY_FAILED;
      data: {
        recipient: string;
        subject: string;
        templateId?: string;
        reason: string;
      };
    }
  | {
      errorType: typeof TaskErrorType.QUICKBOOKS_SYNC_FAILED;
      data: {
        operation: string;
        entityType: string;
        entityId: string;
        reason: string;
      };
    }
  | {
      errorType: typeof TaskErrorType.ARCASA_WORKFLOW_FAILED;
      data: {
        projectId: number;
        workflowStep: string;
        reason: string;
      };
    }
  | {
      errorType: typeof TaskErrorType.TASK_PERMISSION_DENIED;
      data: {
        taskId: string;
        userId: string;
        requiredPermission: string;
        userPermissions: string[];
      };
    }
  | {
      errorType: typeof TaskErrorType.TASK_VALIDATION_FAILED;
      data: {
        taskId: string;
        validationErrors: Array<{
          field: string;
          message: string;
        }>;
      };
    }
  | {
      errorType: typeof TaskErrorType.TASK_BUSINESS_RULE_VIOLATION;
      data: {
        taskId: string;
        rule: string;
        violation: string;
        context: Record<string, unknown>;
      };
    }
  | {
      errorType: typeof TaskErrorType.TASK_DEADLINE_CALCULATION_FAILED;
      data: {
        taskId: string;
        calculationType: string;
        inputs: Record<string, unknown>;
        reason: string;
      };
    }
  | {
      errorType: typeof TaskErrorType.TASK_SCHEDULE_CONFLICT;
      data: {
        taskId: string;
        conflictingTaskIds: number[];
        scheduledTime: Date;
        conflictType: string;
      };
    }
  | {
      errorType: typeof TaskErrorType.TASK_SLA_VIOLATION;
      data: {
        taskId: string;
        slaType: string;
        expectedBy: Date;
        actualTime: Date;
        violationMinutes: number;
      };
    }
  | {
      errorType: typeof TaskErrorType.TASK_CREATE_FAILED;
      data: {
        projectId: number;
        taskType: string;
        reason: string;
      };
    }
  | {
      errorType: typeof TaskErrorType.TASK_UPDATE_FAILED;
      data: {
        taskId: string;
        updateFields: string[];
        reason: string;
      };
    }
  | {
      errorType: typeof TaskErrorType.TASK_DELETE_FAILED;
      data: {
        taskId: string;
        reason: string;
        hasDependent: boolean;
      };
    }
  | {
      errorType: typeof TaskErrorType.TASK_BULK_OPERATION_FAILED;
      data: {
        operation: "create" | "update" | "delete";
        totalCount: number;
        failedCount: number;
        failedIds: string[];
        reason: string;
      };
    }
  | {
      errorType: typeof TaskErrorType.TASK_TRANSACTION_FAILED;
      data: {
        transactionId: string;
        operations: string[];
        failedOperation: string;
        reason: string;
      };
    }
  | {
      errorType: typeof TaskErrorType.TASK_COMPLETION_ADMISSION_READ_FAILED;
      data: { taskId: string };
    }
  | {
      errorType: typeof TaskErrorType.TASK_NOT_A_BLOCKING_TASK;
      data: {
        taskId: string;
        projectId?: number;
      };
    }
  | {
      errorType: typeof TaskErrorType.PROPERTY_MODEL_REQUIRED;
      data: { taskId: string; projectId: number };
    }
  | {
      errorType: typeof TaskErrorType.SITE_VISIT_NOT_ON_TASK_WORKSITE;
      data: { taskId: string; projectId: number; siteVisitProjectId: number };
    }
  | {
      errorType: typeof TaskErrorType.TASK_NOT_ON_BID_PROJECT;
      data: {
        taskId: string;
        projectId?: number;
      };
    }
  | {
      errorType: typeof TaskErrorType.BID_PROJECT_NOT_FOUND;
      data: {
        projectIds: number[];
      };
    }
  | {
      errorType: typeof TaskErrorType.NO_PROJECT_FLOW_FOUND_FOR_SOW_TYPE;
      data: {
        projectId: number;
        sowType: string;
      };
    }
  | {
      errorType: typeof TaskErrorType.TARGET_VERSION_NOT_FOUND;
      data: {
        targetVersion: number;
      };
    }
  | {
      errorType: typeof TaskErrorType.MIGRATION_SCRIPT_NOT_FOUND;
      data: {
        targetVersion: number;
      };
    }
  | {
      errorType: typeof TaskErrorType.WORKFLOW_DEFINITION_MISSING_TASK;
      data: { workflowVersion: number; projectFlow: string; taskType: TaskType };
    }
  | {
      errorType: typeof TaskErrorType.SUNRUN_REDESIGN_WORK_ORDER_NOT_FOUND;
      data: { projectId: number; taskId: string };
    }
  | {
      errorType: typeof TaskErrorType.SUNRUN_REDESIGN_PROJECT_RECORD_NOT_FOUND;
      data: { projectId: number; sunrunRecordId: string };
    }
  | {
      errorType: typeof TaskErrorType.UNKNOWN;
      data: undefined;
    }
  | {
      errorType: typeof TaskErrorType.UNAUTHORIZED_GROUP_COLUMN;
      data: { columnId: string };
    }
  | {
      errorType: typeof TaskErrorType.INVALID_TASK_ATTEMPT_DATA;
      data: {
        field: string;
        issue: string;
        taskId?: string;
      };
    }
  | {
      errorType: typeof TaskErrorType.INVOICE_AUTOMATION_CREATION_FAILED;
      data: {
        projectId: number;
        externalId: string;
        bidId: number;
      };
    }
  | {
      errorType: typeof TaskErrorType.AUTOMATION_ID_NOT_FOUND;
      data: {
        projectId: number;
        bidRequestId: string;
        automationType: string;
      };
    }
  | {
      errorType: typeof TaskErrorType.PARTNER_FLOW_BUYER_MISMATCH;
      data: {
        projectId: number;
        partnerFlow?: string;
        buyerId?: string;
      };
    }
  | {
      errorType: typeof TaskErrorType.INVOICE_BUCKET_NOT_FOUND;
      data: {
        projectId: number;
        partnerFlow?: string;
      };
    }
  | {
      errorType: typeof TaskErrorType.INSTALL_COMPLETED_DATE_BEFORE_STARTED_DATE;
      data: {
        projectId: number;
        installCompletedAt: Date;
        installStartedAt: Date;
      };
    }
  | {
      errorType: typeof TaskErrorType.INSTALL_COMPLETED_DATE_IN_FUTURE;
      data: {
        projectId: number;
        installCompletedAt: Date;
      };
    }
  | {
      errorType: typeof TaskErrorType.REVIEW_REVENUE_ESCROW_NOT_FUNDED;
      data: {
        projectId: number;
        escrowStatus: string;
      };
    }
  | {
      errorType: typeof TaskErrorType.REVIEW_REVENUE_HELD;
      data: { projectId: number; taskId: string; openHoldupCount: number };
    }
  | {
      errorType: typeof TaskErrorType.FINAL_INSPECTION_SCHEDULED_HELD;
      data: { projectId: number; taskId: string; openHoldupCount: number };
    }
  | {
      errorType: typeof TaskErrorType.REVIEW_REVENUE_AUTO_COMPLETE_UNAUTHORIZED;
      data: { personId: string };
    }
  | {
      errorType: typeof TaskErrorType.INSURANCE_ESTIMATE_CREATED_SOFT_DELETED;
      data: { projectId: number };
    }
  | {
      errorType: typeof TaskErrorType.UNEXPECTED_ESTIMATE_TASK_TYPE;
      data: { taskType: string };
    }
  | {
      errorType: typeof TaskErrorType.TASK_TYPE_MISMATCH;
      data: { taskId: string; expectedType: TaskType; actualType: TaskType };
    }
  | {
      errorType: typeof TaskErrorType.RETIRED_TASK_TYPE_REJECTED;
      data: { projectId: number; taskType: TaskType };
    }
  | {
      errorType: typeof TaskErrorType.RETIRED_BLOCKER_TASK_TYPE_REJECTED;
      data: {
        blockingProjectId: number;
        blockedProjectId: number;
        blockingTaskType: TaskType | null;
        blockedTaskType: TaskType | null;
      };
    }
  | {
      errorType: typeof TaskErrorType.VERIFY_COST_BILLS_PENDING_REVIEW;
      data: { projectId: number; taskId: string; pendingReviewCount: number };
    }
  | {
      errorType: typeof TaskErrorType.PANEL_MARKUP_REQUIRED;
      data: { taskId: string; bidProjectId: number };
    }
  | {
      errorType: typeof TaskErrorType.TASK_SUMMARY_NOT_FOUND;
      data: { taskId: string };
    }
  | {
      errorType: typeof TaskErrorType.TASK_SUMMARY_UNAVAILABLE;
      data: { taskId: string };
    }
  | {
      errorType: typeof TaskErrorType.TASK_UNBLOCK_REFUSED;
      data: { taskId: string };
    }
  | {
      errorType: typeof TaskErrorType.TASK_UNBLOCK_REFUSED_SOW_CHANGE_HOLD;
      data: { taskId: string; sowChangeIds: string[] };
    }
  | {
      errorType: typeof TaskErrorType.COLOR_SELECTION_RESET_INELIGIBLE;
      data: { bidId: number; ineligibleReason: ColorSelectionResetIneligibleReason; pendingSince?: string };
    }
  | {
      errorType: typeof TaskErrorType.INSTALL_SCHEDULED_DATE_REQUIRED;
      data: { taskId: string; projectId: number };
    }
  | {
      errorType: typeof TaskErrorType.NTP_FORCE_COMPLETE_ALL_RFQS_REJECTED;
      data: { taskId: string; projectId: number };
    }
  | { errorType: typeof TaskErrorType.TASK_LIST_CURSOR_INVALID; data: undefined };

export const taskErrorDetails: Record<TaskErrorType, ErrorDetails> = {
  TASK_LIST_CURSOR_INVALID: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "An MCP list cursor failed to decode or was issued for different filters",
  },
  TASK_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Task not found in database",
  },
  TASK_OF_TYPE_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Task of a specific type not found for project",
  },
  RELEASE_TASK_DELETED: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription:
      "Attempt holdup release task was soft-deleted before declare committed; the condition is permanent",
  },
  LADDER_NOT_DECLARED_FOR_CANDIDATE: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription:
      "The ladder sweep reached a candidate whose task type declares no ladder. The candidate query filters to declared types and drops exhausted ladders, so reaching this requires both filters to have failed; the sweep refuses rather than firing a rung for an undeclared ladder, which would be an unsolicited contractor message",
  },
  PAYER_STAGE_SIGNAL_TASK_TYPE_UNSUPPORTED: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription:
      "An APPLY_PAYER_STAGE_SIGNALS effect ran for a task type with no payer stage-signal routine; the ladder that fired it is missing from PAYER_STAGE_SIGNAL_TASK_TYPES",
  },
  TASK_ALREADY_COMPLETED: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Task has already been completed",
  },
  TASK_ALREADY_IN_PROGRESS: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Task is already in progress",
  },
  TASK_INVALID_STATUS_TRANSITION: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Invalid task status transition attempted",
  },
  TASK_COMPLETION_FAILED: {
    classification: ErrorClassification.UNEXPECTED_ERROR,
    sentryDescription: "Failed to complete task",
  },
  TASK_COMPLETION_DATE_MISSING: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Expected task completion date is missing",
  },
  TASK_RESET_FAILED: {
    classification: ErrorClassification.UNEXPECTED_ERROR,
    sentryDescription: "Failed to reset task",
  },
  TASK_ATTEMPT_FAILED: {
    classification: ErrorClassification.UNEXPECTED_ERROR,
    sentryDescription: "Failed to attempt task",
  },
  TASK_ATTEMPT_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Task attempt not found",
  },
  ATTEMPT_CONTEXT_NOT_ELIGIBLE: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "This task is not eligible for attempt recording",
  },
  RECORD_ATTEMPT_DEDUPE_KEY_CONFLICT: {
    classification: ErrorClassification.UNEXPECTED_ERROR,
    sentryDescription: "Attempt dedupe key already belongs to a different operation",
  },
  RECORD_ATTEMPT_UNKNOWN_REASON: {
    classification: ErrorClassification.UNEXPECTED_ERROR,
    sentryDescription: "Attempt recorded against a reason the deployed enum no longer recognises",
  },
  RECORD_ATTEMPT_TASK_EVENT_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Attempt workflow could not read back the TaskEvent it just persisted",
  },
  RECORD_ATTEMPT_REASON_UNREADABLE: {
    classification: ErrorClassification.RETRYABLE_ERROR,
    sentryDescription: "Attempt holdup step read back a reason the deployed enum does not recognise yet",
  },
  RETIRED_REASON_BACKFILL_SLA_MATCHERS: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription:
      "The retired attempt reason backfill refused a live run because SLA matchers could fire on its rewrites",
  },
  INSTALL_RESCHEDULE_IN_TRANSACTION: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription:
      "An install reschedule was recorded inside a caller's transaction, so its partner reschedule message would be sent before the task event commits",
  },
  STANDING_HOLDUP_RESOLVE_UNSUPPORTED: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription:
      "A binding declared standingHoldup RESOLVE, whose reader is deliberately unbuilt. Only KEEP ships today; the resolve semantics (which of several standing holdups to end, and under which releaseKind) are designed against the first real consumer, so the arm throws rather than silently keeping the hold",
  },
  RECORD_ATTEMPT_NOTE_CONTRACTOR_ORG_UNRESOLVED: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Attempt note dropped — contractor conversation org unresolved or caller-org mismatch",
  },
  TASK_EVALUATION_FAILED: {
    classification: ErrorClassification.UNEXPECTED_ERROR,
    sentryDescription: "Failed to evaluate task",
  },
  TASK_AUTO_COMPLETE_FAILED: {
    classification: ErrorClassification.UNEXPECTED_ERROR,
    sentryDescription: "Failed to auto-complete task",
  },
  WORKFLOW_VERSION_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Workflow version not found for project",
  },
  WORKFLOW_EVALUATION_FAILED: {
    classification: ErrorClassification.UNEXPECTED_ERROR,
    sentryDescription: "Failed to evaluate workflow",
  },
  WORKFLOW_INVALID_SEQUENCE: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Invalid workflow task sequence",
  },
  WORKFLOW_DEPENDENCY_NOT_MET: {
    classification: ErrorClassification.DEPENDENCY_ERROR,
    sentryDescription: "Task dependencies not met",
  },
  WORKFLOW_PHASE_TRANSITION_FAILED: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Failed to transition workflow phase",
  },
  NO_ACTIVE_WORKFLOW: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "No active workflow found",
  },
  PROJECT_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Project not found",
  },
  PROJECT_EVALUATION_FAILED: {
    classification: ErrorClassification.UNEXPECTED_ERROR,
    sentryDescription: "Failed to evaluate project",
  },
  PROJECT_MISSING_BID_REQUEST_ID: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Project does not have bid request ID to invoice",
  },
  WORK_ORDER_OR_FSD_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Work order or FSD not found",
  },
  PROJECT_TO_BID_REQUEST_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "No project to bid request records found for project",
  },
  BID_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Bid not found",
  },
  BID_RFQ_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Request for quote not found for bid",
  },
  QUOTE_VERSION_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Quote version not found",
  },
  REQUEST_FOR_QUOTE_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Request for quote not found",
  },
  PROJECT_MISSING_PARTNER_FLOW: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Project missing partner flow",
  },
  SITE_VISIT_PROJECT_MISSING_BID_PARTNER_FLOW: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "SITE_VISIT project missing bid partner flow",
  },
  TASK_MISSING_REQUIRED_DATA: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Task missing required data",
  },
  TASK_ORPHANED_RECORD: {
    classification: ErrorClassification.DATABASE_ERROR,
    sentryDescription: "Task record orphaned from parent",
  },
  TASK_DATA_CORRUPTION: {
    classification: ErrorClassification.DATABASE_ERROR,
    sentryDescription: "Task data corruption detected",
  },
  TASK_CONCURRENT_MODIFICATION: {
    classification: ErrorClassification.CONFLICT_ERROR,
    sentryDescription: "Concurrent task modification detected",
  },
  TASK_COMPLETION_RACE_LOST: {
    classification: ErrorClassification.CONFLICT_ERROR,
    sentryDescription: "Task completion lost the CAS race to a concurrent request",
  },
  TERMINAL_ATTEMPT_DISPOSITION_OVERTAKEN: {
    classification: ErrorClassification.CONFLICT_ERROR,
    sentryDescription:
      "Terminal attempt lost its completion race to a different disposition, so its outcome was dropped",
  },
  TASK_COMPLETION_PRECONDITION_FAILED: {
    classification: ErrorClassification.CONFLICT_ERROR,
    sentryDescription: "Task completion refused because the captured task or contractor assignment changed",
  },
  UNKNOWN_COMPLETION_STEP: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "A completion step dispatch named a step that is not in the registry",
  },
  COMPLETION_EFFECT_KEYS_MISMATCH: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "A durable completion's effect-key snapshot no longer matches its task type's effects",
  },
  TASK_DISPOSITION_NOT_ALLOWED_FOR_TYPE: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "A non-resolved completion disposition was sent for an unclassified task type",
  },
  SOW_CHANGE_ABANDONED_WITHOUT_DISPOSITION: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "SOW change completed with a free-text reason and no disposition",
  },
  TASK_FILE_UPLOAD_FAILED: {
    classification: ErrorClassification.INTEGRATION_ERROR,
    sentryDescription: "Failed to upload task file",
  },
  TASK_FILE_PROCESSING_FAILED: {
    classification: ErrorClassification.UNEXPECTED_ERROR,
    sentryDescription: "Failed to process task file",
  },
  TASK_FILE_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Task file not found",
  },
  INVALID_FILE_DOWNLOAD_URL: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Invalid file download URL (SSRF protection)",
  },
  TASK_S3_OPERATION_FAILED: {
    classification: ErrorClassification.INTEGRATION_ERROR,
    sentryDescription: "S3 operation failed for task",
  },
  SUNRUN_API_ERROR: {
    classification: ErrorClassification.INTEGRATION_ERROR,
    sentryDescription: "Sunrun API error",
  },
  SUNRUN_AUTOMATION_FAILED: {
    classification: ErrorClassification.INTEGRATION_ERROR,
    sentryDescription: "Sunrun automation failed",
  },
  HUBSPOT_SYNC_FAILED: {
    classification: ErrorClassification.INTEGRATION_ERROR,
    sentryDescription: "HubSpot sync failed",
  },
  SLACK_NOTIFICATION_FAILED: {
    classification: ErrorClassification.INTEGRATION_ERROR,
    sentryDescription: "Slack notification failed",
  },
  SMS_DELIVERY_FAILED: {
    classification: ErrorClassification.INTEGRATION_ERROR,
    sentryDescription: "SMS delivery failed",
  },
  EMAIL_DELIVERY_FAILED: {
    classification: ErrorClassification.INTEGRATION_ERROR,
    sentryDescription: "Email delivery failed",
  },
  QUICKBOOKS_SYNC_FAILED: {
    classification: ErrorClassification.INTEGRATION_ERROR,
    sentryDescription: "QuickBooks sync failed",
  },
  ARCASA_WORKFLOW_FAILED: {
    classification: ErrorClassification.INTEGRATION_ERROR,
    sentryDescription: "Arcasa workflow failed",
  },
  TASK_PERMISSION_DENIED: {
    classification: ErrorClassification.AUTHORIZATION_ERROR,
    sentryDescription: "Permission denied for task operation",
  },
  TASK_VALIDATION_FAILED: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Task validation failed",
  },
  TASK_BUSINESS_RULE_VIOLATION: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Business rule violation in task",
  },

  TASK_DEADLINE_CALCULATION_FAILED: {
    classification: ErrorClassification.UNEXPECTED_ERROR,
    sentryDescription: "Failed to calculate task deadline",
  },
  TASK_SCHEDULE_CONFLICT: {
    classification: ErrorClassification.CONFLICT_ERROR,
    sentryDescription: "Task schedule conflict detected",
  },
  TASK_SLA_VIOLATION: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Task SLA violation detected",
  },

  TASK_CREATE_FAILED: {
    classification: ErrorClassification.DATABASE_ERROR,
    sentryDescription: "Failed to create task",
  },
  TASK_UPDATE_FAILED: {
    classification: ErrorClassification.DATABASE_ERROR,
    sentryDescription: "Failed to update task",
  },
  TASK_DELETE_FAILED: {
    classification: ErrorClassification.DATABASE_ERROR,
    sentryDescription: "Failed to delete task",
  },
  TASK_BULK_OPERATION_FAILED: {
    classification: ErrorClassification.DATABASE_ERROR,
    sentryDescription: "Bulk task operation failed",
  },
  TASK_TRANSACTION_FAILED: {
    classification: ErrorClassification.DATABASE_ERROR,
    sentryDescription: "Task transaction failed",
  },
  TASK_COMPLETION_ADMISSION_READ_FAILED: {
    classification: ErrorClassification.DATABASE_ERROR,
    sentryDescription: "Task completion admission read failed",
  },

  TASK_NOT_A_BLOCKING_TASK: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Task is not a blocking task",
  },
  PROPERTY_MODEL_REQUIRED: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Task attempted before a property model exists for the worksite",
  },
  SITE_VISIT_NOT_ON_TASK_WORKSITE: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Task attempt named a site visit from a different worksite than the task's project",
  },
  TASK_NOT_ON_BID_PROJECT: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "blocking tasks that block all projects must be created on the bid project",
  },
  BID_PROJECT_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Bid project not found",
  },
  NO_PROJECT_FLOW_FOUND_FOR_SOW_TYPE: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "No project flow found for sow type",
  },
  TARGET_VERSION_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Target version not found",
  },
  MIGRATION_SCRIPT_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Migration script not found",
  },
  WORKFLOW_DEFINITION_MISSING_TASK: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Workflow definition is missing a task",
  },
  INVALID_TASK_ATTEMPT_DATA: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Invalid data provided for task attempt",
  },
  INVOICE_AUTOMATION_CREATION_FAILED: {
    classification: ErrorClassification.INTEGRATION_ERROR,
    sentryDescription: "Failed to create invoice automation",
  },
  AUTOMATION_ID_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Automation ID not found after creation",
  },
  PARTNER_FLOW_BUYER_MISMATCH: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Partner flow buyer does not match expected buyer",
  },
  INVOICE_BUCKET_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Invoice bucket not found for project",
  },
  INSTALL_COMPLETED_DATE_BEFORE_STARTED_DATE: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Install completed date is before install started date",
  },
  INSTALL_COMPLETED_DATE_IN_FUTURE: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Work completed date can't be in the future",
  },
  REVIEW_REVENUE_ESCROW_NOT_FUNDED: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Review revenue blocked - escrow has no funded amount",
  },
  REVIEW_REVENUE_HELD: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Review revenue completion refused: an open holdup still gates the task",
  },
  FINAL_INSPECTION_SCHEDULED_HELD: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Final inspection scheduling refused: an open holdup still gates the task",
  },
  REVIEW_REVENUE_AUTO_COMPLETE_UNAUTHORIZED: {
    classification: ErrorClassification.AUTHORIZATION_ERROR,
    sentryDescription: "Caller lacks reviewRevenueAutoComplete.read for a review revenue dry run",
  },
  PARTNER_FLOW_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Partner flow not found",
  },
  SUNRUN_REDESIGN_WORK_ORDER_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Sunrun redesign work order not found for project",
  },
  SUNRUN_REDESIGN_PROJECT_RECORD_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Sunrun work order is missing its linked Project record for redesign photo upload",
  },
  INSURANCE_ESTIMATE_CREATED_SOFT_DELETED: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "createMissingTasks blocked: INSURANCE_ESTIMATE_CREATED soft-deleted in fanout",
  },
  UNEXPECTED_ESTIMATE_TASK_TYPE: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Unexpected task type encountered in estimate handler",
  },
  TASK_TYPE_MISMATCH: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Task type does not match the expected type for the called helper",
  },
  RETIRED_TASK_TYPE_REJECTED: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Task creation rejected because the task type is retired",
  },
  RETIRED_BLOCKER_TASK_TYPE_REJECTED: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Project blocker creation rejected because a task type on it is retired",
  },
  VERIFY_COST_BILLS_PENDING_REVIEW: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Verify contractor costs completion blocked - review-needed bills still pending approval",
  },
  PANEL_MARKUP_REQUIRED: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Send agreement blocked - partial R&R has no panel removal markup image",
  },
  TASK_SUMMARY_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Task summary not found for task",
  },
  TASK_SUMMARY_UNAVAILABLE: {
    classification: ErrorClassification.UNEXPECTED_ERROR,
    sentryDescription: "Task summary row could not be created or re-read after a lost creation race",
  },
  TASK_UNBLOCK_REFUSED: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Task unblock refused: an open holdup still gates the task",
  },
  TASK_UNBLOCK_REFUSED_SOW_CHANGE_HOLD: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Task unblock refused: a SOW change's revenue hold gates the task",
  },
  TASK_HELD_UP: {
    classification: ErrorClassification.CONFLICT_ERROR,
    sentryDescription: "Task is held up and cannot be accepted until the hold is released",
  },
  // Not AUTHORIZATION_ERROR: the procedure's task gate is the authorization boundary, so a site-visit
  // project off that task's bid is incoherent input, not a caller the gate should have refused.
  SITE_VISIT_PROJECT_NOT_RELATED_TO_TASK: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Availability write named a site-visit project that is not on the gating task's bid",
  },
  UNKNOWN: {
    classification: ErrorClassification.UNEXPECTED_ERROR,
    sentryDescription: "Unknown task error",
  },
  UNAUTHORIZED_GROUP_COLUMN: {
    classification: ErrorClassification.AUTHORIZATION_ERROR,
    sentryDescription: "User is not authorized to group by the requested column",
  },
  COLOR_SELECTION_RESET_INELIGIBLE: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Color selection can't be reset for this bid",
  },
  INSTALL_SCHEDULED_DATE_REQUIRED: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Install scheduled can't be completed until the project has an install date.",
  },
  NTP_FORCE_COMPLETE_ALL_RFQS_REJECTED: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Verify NTP force-complete refused because every RFQ on the bid is rejected",
  },
};
